import { Add } from '@mui/icons-material'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Fade,
  Grid,
  Hidden,
  Typography
} from '@mui/material'
import React from 'react'
import { connect } from 'react-redux'
import CreateReservation from 'components/amenity-booking/create-reservation'
import {
  Announcements,
  DashboardRecentDocuments,
  NivosCarousel,
  TrackedIncidentReportTable
} from 'components/dashboard'
import {
  CreateIncidentReport,
  IncidentReportCard,
  IncidentReportChart
} from 'components/incident-reports'
import { NivosEventItem } from 'components/nivos-events'
import NivosModal from 'components/nivos-modal'
import NivosPaginator from 'components/nivos-paginator'
import {
  Operations as AmenityBookingOperations,
  operations as amenityBookingOperations
} from 'states/dux/amenity-booking/operations'
import { Payload as AmenityBookingPayload } from 'states/dux/amenity-booking/types'
import {
  Operations as AmenityOperations,
  operations as amenityOperations
} from 'states/dux/amenity/operations'
import { Payload as AmenityPayload } from 'states/dux/amenity/types'
import {
  Operations as IncidentReportOperations,
  operations as incidentReportOperations
} from 'states/dux/incident-report/operations'
import { Payload as IncidentReportPayload } from 'states/dux/incident-report/types'
import {
  Operations as LayoutOperations,
  operations as layoutOperations
} from 'states/dux/layout/operations'
import { Payload as LayoutPayload } from 'states/dux/layout/types'
import {
  Operations as ThemeOperations,
  operations as themeOperations
} from 'states/dux/theme/operations'
import { Payload as ThemePayload } from 'states/dux/theme/types'
import State from 'states/types'
import { AmenityBooking } from 'types/amenity-booking'
import { IncidentReport } from 'types/incident-report'
import {
  get,
  mapOperationsToDispatchProps,
  objectifyDateStringFields
} from 'utils/common'

interface StateProps {
  theme: ThemePayload
  layout: LayoutPayload
  incidentReport: IncidentReportPayload
  amenity: AmenityPayload
  amenityBooking: AmenityBookingPayload
}

interface DispatchProps {
  operations: {
    theme: ThemeOperations
    layout: LayoutOperations
    incidentReport: IncidentReportOperations
    amenity: AmenityOperations
    amenityBooking: AmenityBookingOperations
  }
}

type AppProps = StateProps & DispatchProps

const DashboardFC: React.FC<any> = (props: AppProps) => {
  const { theme, layout, incidentReport } = props
  const { amenityBooking, amenity, operations } = props

  const [amenityBookingModal, setABModal] = React.useState(false)

  React.useEffect(() => {
    // Fetch incident reports
    if (incidentReport.data.length < 1) {
      operations.incidentReport.loading(true)
      void get('/report/').then((data: IncidentReport[]) => {
        if (data.length > 0) {
          const parsedData = data.map(report => {
            return objectifyDateStringFields(report)
          })
          operations.incidentReport.set(parsedData)
        }
        operations.incidentReport.loading(false)
      })
    }

    // Fetch available amenities
    if (amenity.data.length < 1) {
      operations.amenity.loading(true)
      void get('/booking/items/amenity/').then(data => {
        if (data.length > 0) {
          operations.amenity.set(data)
        }
        operations.amenity.loading(false)
      })
    }

    // Fetch amenity bookings
    if (amenityBooking.data.length < 1) {
      operations.amenityBooking.loading(true)
      void get('/booking/amenity/').then(data => {
        if (data.length > 0) {
          const parsedData = data.map((item: any) =>
            objectifyDateStringFields(item)
          )
          operations.amenityBooking.set(parsedData)
        }
        operations.amenityBooking.loading(false)
      })
    }
  }, [])

  return (
    <Fade in={true} timeout={1000}>
      <Box
        display={'flex'}
        flexDirection={'column'}
        width={'99%'}
        paddingTop={4}
      >
        <NivosCarousel />
        <Box paddingY={2} />
        <Grid container justifyContent={'center'} spacing={2}>
          <Grid item md={12} lg={6}>
            <Box
              width={1}
              color={theme.secondaryColor}
              display={'flex'}
              flexDirection={'column'}
              alignItems={'center'}
              minHeight={500}
            >
              <Typography variant={'h4'} align={'center'} gutterBottom>
                {layout.language.translations.amenityBooking.toUpperCase()}
              </Typography>
              <Box
                display={'flex'}
                padding={1}
                paddingTop={2}
                width={1}
                justifyContent={'space-evenly'}
              >
                <NivosModal
                  title={'Nivos'}
                  open={amenityBookingModal}
                  setOpen={setABModal}
                >
                  <CreateReservation
                    selectedAmenity={amenity.data[0]}
                    handleClose={() => setABModal(false)}
                  />
                </NivosModal>
                <Hidden mdDown>
                  <Box
                    width={0.45}
                    display={'flex'}
                    flexDirection={'column'}
                    alignItems={'center'}
                  >
                    <Typography gutterBottom variant={'h5'}>
                      {layout.language.translations.myBookings}
                    </Typography>
                    {amenityBooking.loading && <CircularProgress />}
                    {!amenityBooking.loading && (
                      <NivosPaginator
                        itemsPerPage={3}
                        dataList={[...amenityBooking.data]}
                        childElement={NivosEventItem}
                        childPropSetter={(data: AmenityBooking) => {
                          return {
                            event: {
                              eventName: data.amenity.name,
                              date: data.fromDate,
                              description: data.amenity.description
                            }
                          }
                        }}
                      />
                    )}
                  </Box>
                  <Box
                    width={0.45}
                    display={'flex'}
                    flexDirection={'column'}
                    alignItems={'center'}
                  >
                    <Typography variant={'h5'} gutterBottom>
                      {layout.language.translations.createReservation}
                    </Typography>
                    {amenity.data.map((item, index) => (
                      <Box
                        key={index}
                        display={'flex'}
                        alignItems={'center'}
                        justifyContent={'space-between'}
                        width={0.85}
                      >
                        <Typography paddingY={1} fontWeight={'bold'}>
                          {item.name}
                        </Typography>
                        <Chip
                          variant={'filled'}
                          label={item.bookable ? 'Available' : 'Unavailable'}
                          color={'primary'}
                        />
                      </Box>
                    ))}
                    <Box paddingY={1} />
                    <Divider variant={'middle'} sx={{ width: 1 }} />
                    <Box paddingY={1} />
                    <Button
                      variant={'contained'}
                      color={'primary'}
                      onClick={() => setABModal(true)}
                    >
                      {layout.language.translations.createReservation}
                    </Button>
                  </Box>
                </Hidden>
                <Hidden mdUp>
                  <Box
                    width={1}
                    display={'flex'}
                    flexDirection={'column'}
                    alignItems={'center'}
                  >
                    <Typography gutterBottom variant={'h5'}>
                      {layout.language.translations.myBookings}
                    </Typography>
                    {amenityBooking.loading && <CircularProgress />}
                    {!amenityBooking.loading && (
                      <NivosPaginator
                        itemsPerPage={3}
                        dataList={amenityBooking.data}
                        childElement={NivosEventItem}
                        childPropSetter={(data: AmenityBooking) => {
                          return {
                            event: {
                              eventName: data.amenity.name,
                              date: data.fromDate,
                              description: data.amenity.description
                            }
                          }
                        }}
                      />
                    )}
                    <Box paddingY={1} />
                    <Button
                      variant={'contained'}
                      color={'primary'}
                      onClick={() => setABModal(true)}
                    >
                      {layout.language.translations.createReservation}
                    </Button>
                  </Box>
                </Hidden>
              </Box>
            </Box>
          </Grid>
          <Grid item md={12} lg={6}>
            <Box
              width={1}
              color={theme.secondaryColor}
              display={'flex'}
              flexDirection={'column'}
              alignItems={'center'}
              minHeight={500}
              marginX={'auto'}
            >
              <Typography variant={'h4'} align={'center'} gutterBottom>
                {layout.language.translations.incidentReports.toUpperCase()}
              </Typography>
              <Box
                display={'flex'}
                padding={1}
                paddingTop={2}
                width={1}
                justifyContent={'space-evenly'}
                flexWrap={'wrap'}
              >
                <Hidden mdDown>
                  <Box display={'flex'} alignItems={'center'} padding={2}>
                    <IncidentReportChart reportData={incidentReport.data} />
                  </Box>
                  <Box
                    display={'flex'}
                    flexDirection={'column'}
                    alignItems={'center'}
                    width={0.5}
                  >
                    <NivosPaginator
                      dataList={incidentReport.data}
                      childElement={IncidentReportCard}
                      itemsPerPage={3}
                      childPropSetter={(data, index) => {
                        return { report: data, key: index }
                      }}
                    />
                    <CreateIncidentReport
                      buttonProps={{ fullWidth: true, sx: { marginTop: 3 } }}
                    />
                  </Box>
                </Hidden>
                <Hidden mdUp>
                  <Box
                    display={'flex'}
                    flexDirection={'column'}
                    alignItems={'center'}
                    width={1}
                  >
                    <NivosPaginator
                      dataList={incidentReport.data}
                      childElement={IncidentReportCard}
                      itemsPerPage={5}
                      childPropSetter={(data, index) => {
                        return { report: data, key: index }
                      }}
                    />
                    <CreateIncidentReport
                      buttonProps={{ fullWidth: true, sx: { marginTop: 3 } }}
                    />
                  </Box>
                </Hidden>
              </Box>
            </Box>
          </Grid>
          <Grid item md={12} lg={6}>
            <Box
              justifyContent={'center'}
              width={0.95}
              maxWidth={850}
              margin={'auto'}
              display={'flex'}
              alignItems={'center'}
              height={400}
            >
              <DashboardRecentDocuments />
            </Box>
          </Grid>
          <Grid item md={12} lg={6}>
            <Box
              justifyContent={'center'}
              width={1}
              display={'flex'}
              alignItems={'center'}
              borderRadius={'12px'}
              height={400}
            />
          </Grid>
        </Grid>
      </Box>
    </Fade>
  )
}

const DashboardV2FC: React.FC<any> = (props: AppProps) => {
  const { incidentReport, amenityBooking, amenity, operations } = props

  React.useEffect(() => {
    // Fetch incident reports
    if (incidentReport.data.length < 1) {
      operations.incidentReport.loading(true)
      void get('/report/').then((data: IncidentReport[]) => {
        if (data.length > 0) {
          const parsedData = data.map(report => {
            return objectifyDateStringFields(report)
          })
          operations.incidentReport.set(parsedData)
        }
        operations.incidentReport.loading(false)
      })
    }

    // Fetch available amenities
    if (amenity.data.length < 1) {
      operations.amenity.loading(true)
      void get('/booking/items/amenity/').then(data => {
        if (data.length > 0) {
          operations.amenity.set(data)
        }
        operations.amenity.loading(false)
      })
    }

    // Fetch amenity bookings
    if (amenityBooking.data.length < 1) {
      operations.amenityBooking.loading(true)
      void get('/booking/amenity/').then(data => {
        if (data.length > 0) {
          const parsedData = data.map((item: any) =>
            objectifyDateStringFields(item)
          )
          operations.amenityBooking.set(parsedData)
        }
        operations.amenityBooking.loading(false)
      })
    }
  }, [])

  return (
    <Fade in={true} timeout={1000}>
      <Box display={'flex'} flexDirection={'column'} width={'99%'}>
        <Announcements />

        <Box display={'flex'} flexDirection={'column'} padding={4}>
          <Box display={'flex'} alignItems={'center'} paddingRight={4}>
            <Typography
              variant={'h4'}
              padding={2}
              paddingY={6}
              paddingRight={4}
            >
              MY BOOKINGS
            </Typography>
            <Button
              variant={'outlined'}
              size={'large'}
              startIcon={<Add />}
              sx={{ borderRadius: 4 }}
            >
              BOOK NOW
            </Button>
          </Box>
          <Box display={'flex'} overflow={'hidden'} minHeight={200}>
            <Typography
              variant={'h5'}
              fontStyle={'italic'}
              padding={2}
              color={'#9e9d9d'}
            >
              NO BOOKINGS
            </Typography>
          </Box>
        </Box>

        <TrackedIncidentReportTable />
      </Box>
    </Fade>
  )
}

console.log(DashboardFC)

const mapStateToProps = (state: State): StateProps => {
  return {
    theme: state.theme,
    layout: state.layout,
    incidentReport: state.incidentReport,
    amenity: state.amenity,
    amenityBooking: state.amenityBooking
  }
}

const mapDispatchToProps = mapOperationsToDispatchProps({
  theme: themeOperations,
  layout: layoutOperations,
  incidentReport: incidentReportOperations,
  amenity: amenityOperations,
  amenityBooking: amenityBookingOperations
})

const Dashboard = connect(mapStateToProps, mapDispatchToProps)(DashboardV2FC)

export default Dashboard
